/**
 * Chat Message Component
 *
 * Displays individual chat messages with CP Admin 2077 styling.
 * Supports user, assistant, system, and tool message types.
 */

import React from 'react';
import type { ChatMessage as ChatMessageType } from '../../contexts/ChatContext';

interface ChatMessageProps {
  message: ChatMessageType;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isTool = message.role === 'tool';
  const isSystem = message.role === 'system';

  // Format timestamp
  const timestamp = new Date(message.createdAt).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
    >
      <div
        className={`
          max-w-[85%] px-4 py-3
          ${isUser
            ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
            : isAssistant
              ? 'bg-slate-800 border border-slate-700 text-slate-100'
              : isTool
                ? 'bg-purple-900/50 border border-purple-700/50 text-purple-100 font-mono text-sm'
                : 'bg-cyan-900/50 border border-cyan-700/50 text-cyan-100'
          }
        `}
        style={{ clipPath: isUser
          ? 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)'
          : 'polygon(0 0, 100% 0, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
        }}
      >
        {/* Role indicator for non-user messages */}
        {!isUser && (
          <div className="flex items-center gap-2 mb-1.5 text-xs opacity-70">
            <span className={`
              w-2 h-2 rounded-full
              ${isAssistant ? 'bg-orange-500' : isTool ? 'bg-purple-500' : 'bg-cyan-500'}
            `} />
            <span className="uppercase tracking-wider">
              {isAssistant ? 'AI Assistant' : isTool ? 'Tool Result' : 'System'}
            </span>
          </div>
        )}

        {/* Message content */}
        <div className="whitespace-pre-wrap break-words">
          {message.content}
        </div>

        {/* Tool calls display */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-600/50">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
              Tools Used
            </div>
            {message.toolCalls.map((tool, index) => (
              <div
                key={index}
                className="bg-slate-900/50 px-2 py-1 mt-1 text-xs font-mono text-cyan-400"
              >
                <span className="text-orange-400">{tool.toolName}</span>
                <span className="text-slate-500">(</span>
                <span className="text-slate-300">
                  {tool.args && Object.keys(tool.args).length > 0
                    ? JSON.stringify(tool.args).slice(1, -1).slice(0, 50) + '...'
                    : ''
                  }
                </span>
                <span className="text-slate-500">)</span>
              </div>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <div className={`
          text-[10px] mt-2
          ${isUser ? 'text-orange-200/70' : 'text-slate-500'}
          text-right
        `}>
          {timestamp}
        </div>
      </div>
    </div>
  );
}

export default ChatMessage;
